import { Box, Toolbar, CircularProgress } from "@mui/material";
import {
  Outlet,
  createFileRoute,
  redirect,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { useState, useEffect } from "react";

import {
  useGetLatestTerm,
  useGetTermAcceptanceByUser,
} from "../../api/bpm/endpoints";
import type { TermsOfUseDto } from "../../api/bpm/model";
import { useAuth } from "../../auth";
import LayoutHeader from "../../components/LayoutHeader";
import LayoutSidebar from "../../components/LayoutSidebar";
import { FolderCacheProvider } from "../../context/FolderCacheContext";
import { isSebraeMG } from "../../utils/envUtils";
import {
  isTermsAcceptancePath,
  shouldRedirectToTerms,
} from "../../utils/termsAccess";

export const Route = createFileRoute("/__auth")({
  component: AuthLayout,
  beforeLoad: ({ context, location }) => {
    if (context.auth.isLoading) return;

    if (!context.auth?.isAuthenticated) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }
  },
});

function AuthLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isSwitchingTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isOnTermsPage = isTermsAcceptancePath(location.pathname);
  const shouldCheckTerms = !isOnTermsPage && !isSebraeMG();
  const { data: latestTermResponse } = useGetLatestTerm(undefined, {
    query: {
      enabled: shouldCheckTerms,
      staleTime: 1000 * 60 * 60,
      gcTime: 1000 * 60 * 60 * 24,
      retry: 1,
    },
  });
  const latestTerm = latestTermResponse as unknown as TermsOfUseDto | undefined;
  const latestTermId = latestTerm?.id;
  const { error: acceptanceError, isError: termsNotAccepted } =
    useGetTermAcceptanceByUser(latestTermId ?? "", {
      query: {
        enabled: shouldCheckTerms && Boolean(latestTermId),
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 24,
        retry: 1,
      },
    });

  useEffect(() => {
    if (isSebraeMG()) return;
    if (
      !isOnTermsPage &&
      termsNotAccepted &&
      shouldRedirectToTerms(latestTermId, acceptanceError)
    ) {
      navigate({ to: "/terms" });
    }
  }, [
    termsNotAccepted,
    acceptanceError,
    latestTermId,
    isOnTermsPage,
    navigate,
  ]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <FolderCacheProvider>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          overflow: "hidden",
          height: "100%",
          width: "100%",
        }}
      >
        {!isOnTermsPage && <LayoutHeader onMenuClick={handleDrawerToggle} />}

        {!isOnTermsPage && (
          <LayoutSidebar
            mobileOpen={mobileOpen}
            onMobileClose={handleDrawerToggle}
          />
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          {!isOnTermsPage && <Toolbar sx={{ displayPrint: "none" }} />}
          <Box
            component="main"
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              overflow: "auto",
              px: isOnTermsPage ? 0 : { xs: 1, sm: 2, lg: 3 },
              py: isOnTermsPage ? 0 : { xs: 1, sm: 2 },
            }}
          >
            <Outlet />
          </Box>
        </Box>

        {isSwitchingTenant && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: (theme) => theme.zIndex.drawer + 2,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mt: { xs: "56px", sm: "64px" },
              height: { xs: "calc(100% - 56px)", sm: "calc(100% - 64px)" },
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </FolderCacheProvider>
  );
}
